import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
  Badge,
  Modal,
} from "react-bootstrap";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { projectData } from "../services/Constants";

// Time slots (2 hour blocks)
const TIME_SLOTS = [
  { id: "0-2", label: "12:00 AM - 2:00 AM" },
  { id: "2-4", label: "2:00 AM - 4:00 AM" },
  { id: "4-6", label: "4:00 AM - 6:00 AM" },
  { id: "6-8", label: "6:00 AM - 8:00 AM" },
  { id: "8-10", label: "8:00 AM - 10:00 AM" },
  { id: "10-12", label: "10:00 AM - 12:00 PM" },
  { id: "12-14", label: "12:00 PM - 2:00 PM" },
  { id: "14-16", label: "2:00 PM - 4:00 PM" },
  { id: "16-18", label: "4:00 PM - 6:00 PM" },
  { id: "18-20", label: "6:00 PM - 8:00 PM" },
  { id: "20-22", label: "8:00 PM - 10:00 PM" },
  { id: "22-24", label: "10:00 PM - 12:00 AM" },
];

const AvailabilityManager = () => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [availabilityData, setAvailabilityData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dateToDelete, setDateToDelete] = useState(null);
  const { t } = useTranslation();

  // Fetch existing availability data
  useEffect(() => {
    fetchAvailabilityData();
  }, []);

  const fetchAvailabilityData = async () => {
    try {
      setLoading(true);
      setError(null);

      const availabilityRef = collection(
        db,
        projectData().AVAILABILITY_COLLECTION,
      );
      const querySnapshot = await getDocs(availabilityRef);

      const availabilities = [];
      querySnapshot.forEach((doc) => {
        availabilities.push({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date.toDate(), // Convert Firestore timestamp to JS Date
        });
      });

      // Sort by date (most recent first)
      availabilities.sort((a, b) => a.date - b.date);
      setAvailabilityData(availabilities);
    } catch (err) {
      console.error("Error fetching availability data:", err);
      setError("Failed to load availability data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSlotToggle = (slotId) => {
    if (selectedSlots.includes(slotId)) {
      setSelectedSlots(selectedSlots.filter((id) => id !== slotId));
    } else {
      setSelectedSlots([...selectedSlots, slotId]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDate || selectedSlots.length === 0) {
      setError("Please select a date and at least one time slot.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Check if date already exists
      const dateExists = availabilityData.some(
        (item) =>
          new Date(item.date).toDateString() ===
          new Date(selectedDate).toDateString(),
      );

      if (dateExists) {
        // Update existing date
        const existingDoc = availabilityData.find(
          (item) =>
            new Date(item.date).toDateString() ===
            new Date(selectedDate).toDateString(),
        );

        await updateDoc(
          doc(db, projectData().AVAILABILITY_COLLECTION, existingDoc.id),
          {
            timeSlots: selectedSlots,
            updatedAt: Timestamp.now(),
          },
        );

        setSuccess(
          `Availability for ${new Date(
            selectedDate,
          ).toLocaleDateString()} has been updated.`,
        );
      } else {
        // Create new date entry
        await addDoc(collection(db, projectData().AVAILABILITY_COLLECTION), {
          date: Timestamp.fromDate(new Date(selectedDate)),
          timeSlots: selectedSlots,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });

        setSuccess(
          `Availability for ${new Date(
            selectedDate,
          ).toLocaleDateString()} has been added.`,
        );
      }

      // Reset form
      setSelectedDate("");
      setSelectedSlots([]);

      // Refresh data
      await fetchAvailabilityData();
    } catch (err) {
      console.error("Error saving availability:", err);
      setError("Failed to save availability. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (date) => {
    setDateToDelete(date);
    setShowDeleteModal(true);
  };

  const handleDeleteAvailability = async () => {
    if (!dateToDelete) return;

    try {
      setLoading(true);
      setError(null);

      await deleteDoc(
        doc(db, projectData().AVAILABILITY_COLLECTION, dateToDelete.id),
      );

      setSuccess(
        `Availability for ${dateToDelete.date.toLocaleDateString()} has been deleted.`,
      );
      await fetchAvailabilityData();
      setShowDeleteModal(false);
    } catch (err) {
      console.error("Error deleting availability:", err);
      setError("Failed to delete availability. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = (availabilityItem) => {
    const jsDate = availabilityItem.date;
    // Format YYYY-MM-DD for input
    const formattedDate = jsDate.toISOString().split("T")[0];
    setSelectedDate(formattedDate);
    setSelectedSlots(availabilityItem.timeSlots || []);
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Add new functions for select/unselect all
  const handleSelectAll = () => {
    setSelectedSlots(TIME_SLOTS.map((slot) => slot.id));
  };

  const handleUnselectAll = () => {
    setSelectedSlots([]);
  };

  return (
    <Container fluid>
      <Card className="mb-4">
        <Card.Header as="h5" className="bg-primary text-white">
          {t("ManageDoctorAvailability")}
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="availabilityDate">
                  <Form.Label>{t("SelectDate")}</Form.Label>
                  <Form.Control
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={getMinDate()}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label>{t("SelectAvailableTimeSlots")}</Form.Label>
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleSelectAll}
                      className="me-2"
                    >
                      {t("SelectAll")}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleUnselectAll}
                    >
                      {t("UnselectAll")}
                    </Button>
                  </div>
                </div>
                <div className="mb-3 d-flex flex-wrap">
                  {TIME_SLOTS.map((slot) => (
                    <Form.Check
                      key={slot.id}
                      type="checkbox"
                      id={`slot-${slot.id}`}
                      label={t(`Time${slot.id.replace("-", "To")}`)}
                      className="me-3 mb-2"
                      checked={selectedSlots.includes(slot.id)}
                      onChange={() => handleSlotToggle(slot.id)}
                    />
                  ))}
                </div>
              </Col>
            </Row>

            <div className="mt-3">
              <Button
                type="submit"
                variant="primary"
                disabled={
                  loading || !selectedDate || selectedSlots.length === 0
                }
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="ms-2">{t("Saving")}</span>
                  </>
                ) : (
                  t("SaveAvailability")
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header as="h5" className="bg-primary text-white">
          {t("ScheduledAvailability")}
        </Card.Header>
        <Card.Body>
          {loading && !availabilityData.length ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">{t("LoadingAvailability")}</p>
            </div>
          ) : availabilityData.length === 0 ? (
            <Alert variant="info">{t("NoAvailability")}</Alert>
          ) : (
            <Table striped bordered hover responsive className="mt-3">
              <thead>
                <tr>
                  <th>{t("Date")}</th>
                  <th>{t("AvailableTimeSlots")}</th>
                  <th>{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {availabilityData.map((item) => (
                  <tr key={item.id}>
                    <td>{item.date.toLocaleDateString()}</td>
                    <td>
                      {item.timeSlots && item.timeSlots.length > 0 ? (
                        <div className="d-flex flex-wrap">
                          {item.timeSlots.map((slotId) => {
                            const slot = TIME_SLOTS.find(
                              (s) => s.id === slotId,
                            );
                            return (
                              <Badge
                                bg="info"
                                className="me-2 mb-2"
                                key={slotId}
                              >
                                {slot
                                  ? t(`Time${slotId.replace("-", "To")}`)
                                  : slotId}
                              </Badge>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-muted">
                          {t("NoTimeSlotsSpecified")}
                        </span>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleDateClick(item)}
                      >
                        {t("Edit")}
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => openDeleteModal(item)}
                      >
                        {t("Delete")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("ConfirmDelete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {dateToDelete && (
            <p>
              {t("DeleteConfirmation")} {dateToDelete.date.toLocaleDateString()}
              ? {t("CannotBeUndone")}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {t("Cancel")}
          </Button>
          <Button variant="danger" onClick={handleDeleteAvailability}>
            {t("DeleteAvailability")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AvailabilityManager;
